/**
 * Presentation-only: map persisted order / profile state to a customer-safe offer label.
 * Does not mutate LDS deal ids or API payloads.
 */

const HR_PREFIX_RE = /^hr[\s\-_:]*/i;

/**
 * @param {unknown} bookingRef
 * @returns {string | null}
 */
export function formatHrLabel(bookingRef) {
  if (bookingRef == null) return null;
  let s = String(bookingRef).trim();
  if (!s) return null;
  s = s.replace(HR_PREFIX_RE, "");
  const digits = s.replace(/\D/g, "");
  if (digits.length === 8 && digits[0] !== "0") {
    return `HR-${digits}`;
  }
  return null;
}

/**
 * Opaque LDS deal token (e.g. base64 JSON) — must not be shown as "מספר הצעה".
 * @param {unknown} s
 * @returns {boolean}
 */
export function isLikelyOpaqueLdsDealToken(s) {
  const t = typeof s === "string" ? s.trim() : String(s ?? "").trim();
  if (!t) return false;
  if (t.startsWith("eyJ")) return true;
  if (t.length >= 48 && /^[A-Za-z0-9+/=_-]+$/.test(t)) return true;
  return false;
}

function firstNonEmptyTrimmed(...vals) {
  for (const v of vals) {
    if (v == null) continue;
    const t = String(v).trim();
    if (t) return t;
  }
  return null;
}

/**
 * @param {unknown} s
 * @returns {boolean}
 */
function isSafeLegacyOfferToken(s) {
  const t = firstNonEmptyTrimmed(s);
  if (!t) return false;
  if (isLikelyOpaqueLdsDealToken(t)) return false;
  if (formatHrLabel(t)) return true;
  const compact = t.replace(/\s/g, "");
  if (/^HR-[1-9]\d{7}$/i.test(compact)) return true;
  return true;
}

function pickPersistedOfferNumberCandidates(order) {
  if (!order || typeof order !== "object") return [];
  const o = /** @type {Record<string, unknown>} */ (order);
  const op = o.ourprice ?? o.ourPrice ?? {};
  const rootCqd = o.customer_quote_display ?? o.customerQuoteDisplay;
  const opRec = op && typeof op === "object" ? /** @type {Record<string, unknown>} */ (op) : {};
  const cqOp =
    opRec.customer_quote_display && typeof opRec.customer_quote_display === "object"
      ? /** @type {Record<string, unknown>} */ (opRec.customer_quote_display)
      : {};
  const cqCamel =
    opRec.customerQuoteDisplay && typeof opRec.customerQuoteDisplay === "object"
      ? /** @type {Record<string, unknown>} */ (opRec.customerQuoteDisplay)
      : {};
  const rootRec =
    rootCqd && typeof rootCqd === "object"
      ? /** @type {Record<string, unknown>} */ (rootCqd)
      : {};

  return [
    cqCamel.offerNumber,
    cqCamel.offer_number,
    cqOp.offer_number,
    rootRec.offer_number,
  ];
}

/**
 * Merge saved LDS order row with selected-result deal id for display resolution only.
 * @param {Record<string, unknown> | null | undefined} savedOrder
 * @param {Record<string, unknown> | null | undefined} selectedResult
 */
export function mergeLdsSelectedResultForOfferDisplay(savedOrder, selectedResult) {
  const base =
    savedOrder && typeof savedOrder === "object"
      ? { .../** @type {Record<string, unknown>} */ (savedOrder) }
      : {};
  const sid =
    (selectedResult && typeof selectedResult === "object"
      ? /** @type {Record<string, unknown>} */ (selectedResult).dealId ??
        /** @type {Record<string, unknown>} */ (selectedResult).id
      : null) ?? null;
  if (sid != null && String(sid).trim()) {
    const t = String(sid).trim();
    if (base.dealId == null || !String(base.dealId).trim()) base.dealId = t;
    if (base.dealid == null || !String(base.dealid).trim()) base.dealid = t;
    if (base.deal_id == null || !String(base.deal_id).trim()) base.deal_id = t;
  }
  return base;
}

/**
 * @param {Record<string, unknown> | null | undefined} order
 * @returns {{
 *   show: boolean,
 *   label: string,
 *   value: string,
 *   kind: "booking_ref" | "legacy_offer" | "hidden";
 * }}
 */
export function resolveCustomerFacingOfferNumber(order) {
  if (!order || typeof order !== "object") {
    return { show: false, label: "", value: "", kind: "hidden" };
  }
  const o = /** @type {Record<string, unknown>} */ (order);

  const displayRaw = firstNonEmptyTrimmed(
    o.booking_ref_display,
    o.bookingRefDisplay
  );
  if (displayRaw) {
    const normalized = /^hr-/i.test(displayRaw)
      ? displayRaw.replace(/^hr-/i, "HR-")
      : formatHrLabel(displayRaw) || displayRaw;
    return {
      show: true,
      label: "מספר הצעה",
      value: normalized,
      kind: "booking_ref",
    };
  }

  const br = firstNonEmptyTrimmed(o.booking_ref, o.bookingRef);
  const hr = formatHrLabel(br);
  if (hr) {
    return {
      show: true,
      label: "מספר הצעה",
      value: hr,
      kind: "booking_ref",
    };
  }

  for (const c of pickPersistedOfferNumberCandidates(o)) {
    const t = firstNonEmptyTrimmed(c);
    if (t && isSafeLegacyOfferToken(t)) {
      const v = formatHrLabel(t) || t;
      return {
        show: true,
        label: "מספר הצעה",
        value: v,
        kind: "legacy_offer",
      };
    }
  }

  const dealFallback = firstNonEmptyTrimmed(
    o.deal_id,
    o.dealId,
    o.dealid,
    o.selectedHotelPackage?.packageID
  );

  if (dealFallback && isSafeLegacyOfferToken(dealFallback)) {
    return {
      show: true,
      label: "מספר הצעה",
      value: dealFallback,
      kind: "legacy_offer",
    };
  }

  return { show: false, label: "", value: "", kind: "hidden" };
}
